import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, AlertCircle, Shield, Bell } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'mood', label: 'Mood', defaultOp: 'lte' },
  { key: 'energy', label: 'Energy', defaultOp: 'lte' },
  { key: 'stress', label: 'Stress', defaultOp: 'gte' },
  { key: 'sleep', label: 'Sleep Quality', defaultOp: 'lte' },
  { key: 'work_life', label: 'Work-Life Balance', defaultOp: 'lte' },
];

const OP_OPTIONS = [
  { value: 'lte', label: 'falls to or below' },
  { value: 'gte', label: 'rises to or above' },
];

const LOOKBACK_OPTIONS = [3, 5, 7, 14, 21, 30];

export default function WelfareSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/welfare/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to load welfare settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateThreshold = (metricKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [metricKey]: {
          ...prev.thresholds[metricKey],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API}/welfare/settings`, {
        alerts_enabled: settings.alerts_enabled,
        thresholds: settings.thresholds,
        match_day_reminders: settings.match_day_reminders,
        ai_chat_opt_in: settings.ai_chat_opt_in,
        share_anonymous_aggregates: settings.share_anonymous_aggregates,
      });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="welfare-settings-container">
      {/* Alerts master toggle + thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
            Smart alerts
          </CardTitle>
          <p className="text-sm text-gray-500">Get a gentle heads-up when one of your metrics drifts.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between" data-testid="welfare-alerts-master-toggle">
            <div>
              <p className="font-medium text-gray-900">Enable alerts</p>
              <p className="text-xs text-gray-500">Master switch for all thresholds below.</p>
            </div>
            <Switch
              checked={settings.alerts_enabled}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, alerts_enabled: v }))}
              data-testid="welfare-alerts-enabled-switch"
            />
          </div>

          <div className="space-y-5">
            {METRICS.map(m => {
              const t = settings.thresholds[m.key] || { enabled: false, op: m.defaultOp, value: 5, lookback_days: 7 };
              return (
                <div key={m.key} className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid={`welfare-threshold-${m.key}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{m.label}</p>
                    <Switch
                      checked={t.enabled}
                      onCheckedChange={(v) => updateThreshold(m.key, 'enabled', v)}
                      disabled={!settings.alerts_enabled}
                      data-testid={`welfare-threshold-${m.key}-enabled`}
                    />
                  </div>
                  {t.enabled && settings.alerts_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Trigger when</label>
                        <Select value={t.op} onValueChange={(v) => updateThreshold(m.key, 'op', v)}>
                          <SelectTrigger data-testid={`welfare-threshold-${m.key}-op`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {OP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Value (1-10): <span className="font-bold tabular-nums">{t.value}</span></label>
                        <Slider
                          min={1}
                          max={10}
                          step={1}
                          value={[t.value]}
                          onValueChange={(v) => updateThreshold(m.key, 'value', v[0])}
                          className="mt-2"
                          data-testid={`welfare-threshold-${m.key}-value`}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Over the last</label>
                        <Select value={String(t.lookback_days)} onValueChange={(v) => updateThreshold(m.key, 'lookback_days', parseInt(v, 10))}>
                          <SelectTrigger data-testid={`welfare-threshold-${m.key}-lookback`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {LOOKBACK_OPTIONS.map(d => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reminders */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
            Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between" data-testid="welfare-match-reminders">
            <div>
              <p className="font-medium text-gray-900">Match-day check-in reminder</p>
              <p className="text-xs text-gray-500">Show a gentle dashboard prompt to check in on match days.</p>
            </div>
            <Switch
              checked={settings.match_day_reminders}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, match_day_reminders: v }))}
              data-testid="welfare-match-reminders-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
            Privacy
          </CardTitle>
          <p className="text-sm text-gray-500">Your welfare data is strictly private. Only you can see it.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between" data-testid="welfare-ai-chat-opt-in">
            <div>
              <p className="font-medium text-gray-900">Enable AI support chat</p>
              <p className="text-xs text-gray-500">Opt-in chat (coming soon) about coaching wellbeing. Not a substitute for professional help.</p>
            </div>
            <Switch
              checked={settings.ai_chat_opt_in}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, ai_chat_opt_in: v }))}
              data-testid="welfare-ai-chat-opt-in-switch"
            />
          </div>
          <div className="flex items-center justify-between" data-testid="welfare-anonymous-aggregates">
            <div>
              <p className="font-medium text-gray-900">Share anonymous aggregates</p>
              <p className="text-xs text-gray-500">Helps us improve coach wellbeing tools. No identifiable data is shared. Off = your data is completely excluded.</p>
            </div>
            <Switch
              checked={settings.share_anonymous_aggregates}
              onCheckedChange={(v) => setSettings(prev => ({ ...prev, share_anonymous_aggregates: v }))}
              data-testid="welfare-anonymous-aggregates-switch"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-rose-500 hover:bg-rose-600 text-white"
          data-testid="welfare-settings-save-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save Settings</>
          )}
        </Button>
      </div>
    </div>
  );
}
